
import React, { useRef, useState, useEffect } from 'react';
import { createStudent, patchStudent, getDocumentosEstudiante, subirFotoEstudiante } from '../../api/students';
import { getEstudianteBecas } from '../../api/becas';
import InformacionGeneral from './tabs/InformacionGeneral';
import InformacionEscolar from './tabs/InformacionEscolar';
import Calificaciones from './tabs/Calificaciones';
import EstadoFinanciero from './tabs/EstadoFinanciero';
import Documentos from './tabs/Documentos';
import FichaMedica from './tabs/FichaMedica';
import ExpedienteAlumno from './tabs/ExpedienteAlumno';
import styles from '../../styles/EstudiantesStyles/Estudiantes.module.css';
import programStyles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import { normalizeTurno } from '../../constants/turnos';
import { normalizeModalidad } from '../../constants/modalidades';

// TODO: Importa la función mapStudentFormToPayload desde donde esté definida
// import { mapStudentFormToPayload } from 'ruta/del/archivo';

// ...existing code...
// Función para mapear el formulario al payload esperado por el backend
// usando solo los campos que realmente existen en EscEstudiante.
function mapStudentFormToPayload(originalStudent, generalForm = {}, escolarForm = {}, isEditMode = false) {
  // Mapeo de valores de sexo (el formulario puede usar índices o valores)
  const mapSexo = (value) => {
    if (!value) return null;
    // Si ya es M, F, O retornarlo tal cual
    if (['M', 'F', 'O'].includes(value)) return value;
    // Si viene como índice numérico, mapearlo
    const sexoMap = { '0': 'M', '1': 'F', '2': 'O', 'Masculino': 'M', 'Femenino': 'F', 'Otro': 'O' };
    return sexoMap[value] || value;
  };

  // Helper para manejar strings vacíos - convertirlos a null para evitar conflictos de unicidad
  const cleanString = (value) => {
    if (value === undefined || value === null || value === '') return null;
    return String(value).trim() || null;
  };

  const payload = {
    // Datos personales
    nombre_completo: generalForm.nombre_completo || originalStudent.nombre_completo,
    curp: cleanString(generalForm.curp) ?? cleanString(originalStudent.curp),
    lugar_nacimiento: cleanString(generalForm.lugar_nacimiento) ?? cleanString(originalStudent.lugar_nacimiento),
    sexo: mapSexo(generalForm.sexo) || mapSexo(originalStudent.sexo) || 'M',
    fecha_nacimiento: generalForm.fecha_nacimiento || originalStudent.fecha_nacimiento,
    estado_civil: cleanString(generalForm.estado_civil) ?? cleanString(originalStudent.estado_civil),
    nacionalidad: cleanString(generalForm.nacionalidad) ?? cleanString(originalStudent.nacionalidad),
    pais_origen: cleanString(generalForm.pais_origen) ?? cleanString(originalStudent.pais_origen),
    email_personal: cleanString(generalForm.email_personal) ?? cleanString(originalStudent.email_personal),
    email_institucional: cleanString(generalForm.email_institucional) ?? cleanString(originalStudent.email_institucional),
    telefono_principal: cleanString(generalForm.telefono_contacto) ?? cleanString(originalStudent.telefono_principal),
    telefono_emergencia: cleanString(generalForm.telefono_emergencia) ?? cleanString(originalStudent.telefono_emergencia),
    contacto_emergencia: cleanString(generalForm.contacto) ?? cleanString(originalStudent.contacto_emergencia),

    // Domicilio
    calle: cleanString(generalForm.calle) ?? cleanString(originalStudent.calle),
    colonia: cleanString(generalForm.colonia) ?? cleanString(originalStudent.colonia),
    ciudad: cleanString(generalForm.ciudad) ?? cleanString(originalStudent.ciudad),
    estado: cleanString(generalForm.estado_direccion) ?? cleanString(originalStudent.estado),
    codigo_postal: cleanString(generalForm.codigo_postal) ?? cleanString(originalStudent.codigo_postal),
    pais: cleanString(generalForm.pais) ?? cleanString(originalStudent.pais),

    // Datos de Padres o Tutores
    nombre_padre: cleanString(generalForm.nombre_padre) ?? cleanString(originalStudent.nombre_padre),
    ocupacion_padre: cleanString(generalForm.ocupacion_padre) ?? cleanString(originalStudent.ocupacion_padre),
    nombre_madre: cleanString(generalForm.nombre_madre) ?? cleanString(originalStudent.nombre_madre),
    ocupacion_madre: cleanString(generalForm.ocupacion_madre) ?? cleanString(originalStudent.ocupacion_madre),
    ingreso_mensual_familiar: generalForm.ingreso_mensual_familiar !== undefined && generalForm.ingreso_mensual_familiar !== '' 
      ? parseFloat(generalForm.ingreso_mensual_familiar) 
      : originalStudent.ingreso_mensual_familiar,

    // Académicos mínimos que podemos tocar aquí
    // NO enviar matrícula en modo edición para evitar error de duplicación
    ...(isEditMode ? {} : { matricula: escolarForm.matricula || originalStudent.matricula }),
      // Mapear promedio general del formulario al campo promedio_procedencia
      promedio_procedencia: escolarForm.promedio_general !== undefined && escolarForm.promedio_general !== '' ? escolarForm.promedio_general : undefined,
    // Ciclo de ingreso (FK a CatCicloEscolar)
    // IMPORTANTE: En modo edit, NO enviamos ciclo_ingreso porque es un valor histórico
    // inmutable que no debe cambiar después de la inscripción inicial del estudiante.
    // Incluir solo en modo create.
    ...(isEditMode
      ? {}
      : {
          ciclo_ingreso: escolarForm.ciclo_ingreso
            ? (parseInt(escolarForm.ciclo_ingreso, 10) || escolarForm.ciclo_ingreso)
            : originalStudent.ciclo_ingreso,
        }),
    // Institución (FK)
    id_institucion:
      escolarForm.institucion && !isNaN(Number(escolarForm.institucion)) && escolarForm.institucion !== ''
        ? Number(escolarForm.institucion)
        : null,
    // Campus (FK)
    id_campus: (escolarForm.campus && !isNaN(Number(escolarForm.campus)))
      ? Number(escolarForm.campus)
      : (originalStudent.id_campus && (originalStudent.id_campus.id_campus || originalStudent.id_campus)),
    // Programa académico actual (FK)
    id_programa_actual: (escolarForm.programa && !isNaN(Number(escolarForm.programa)))
      ? Number(escolarForm.programa)
      : (originalStudent.id_programa_actual && (originalStudent.id_programa_actual.id_programa || originalStudent.id_programa_actual)),

    // Tipo de Estudiante
    tipo_estudiante: cleanString(escolarForm.tipo_estudiante) ?? cleanString(originalStudent.tipo_estudiante),
    // Turno académico vigente (MATUTINO|NOCTURNO|SABATINO|EN_LINEA)
    turno: (normalizeTurno(escolarForm.turno) || normalizeTurno(originalStudent.turno)) || null,
    // Modalidad académica (PRESENCIAL|EN_LINEA)
    modalidad: (normalizeModalidad(escolarForm.modalidad) || normalizeModalidad(originalStudent.modalidad)) || null,
    // Estatus del estudiante (controla también el acceso al Portal de Estudiantes).
    // Se envía si el usuario lo modificó explícitamente o si ya tiene un valor.
    ...((escolarForm.estatus_estudiante || originalStudent.estatus_estudiante)
      ? { estatus_estudiante: String(escolarForm.estatus_estudiante || originalStudent.estatus_estudiante).toUpperCase() }
      : {}),
  };

    // Mapear la sección académica seleccionada al campo id_seccion del backend
    if (escolarForm.seccion) {
      payload.id_seccion = parseInt(escolarForm.seccion, 10) || escolarForm.seccion;
    }

  // Limpiar undefined para no enviar campos basura al backend
  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined || payload[key] === null) {
      delete payload[key];
    }
  });

  return payload;
}

function StudentDetailModal({ student, onClose, mode = 'view', onSave, readOnly = false }) {
      // Estado para campos con error
      const [erroredFields, setErroredFields] = useState([]);
      // URL de la fotografía de perfil del estudiante (avatar)
      const [fotoUrl, setFotoUrl] = useState(student?.foto_perfil_url || null);
      const [uploadingFotoPerfil, setUploadingFotoPerfil] = useState(false);
      // Estado para becas del estudiante
      const [becasActivas, setBecasActivas] = useState([]);
      const [cargandoBecas, setCargandoBecas] = useState(false);
      const fotoPerfilInputRef = useRef(null);
      const estudianteIdRef = student?.id_estudiante || student?.id;

      const getApiBase = React.useCallback(() => {
        const envBase = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
        if (envBase) return envBase;
        if (typeof window !== 'undefined' && window.location?.origin) {
          return `${window.location.origin}/api`;
        }
        return '';
      }, []);

      const resolveFotoUrlFromDoc = React.useCallback((doc) => {
        if (!doc) return null;
        if (doc.archivo_url) return doc.archivo_url;
        if (doc.url) {
          const mediaBase = getApiBase().replace(/\/api$/, '');
          return `${mediaBase}${doc.url}`;
        }
        if (doc.ruta_archivo) {
          const mediaBase = getApiBase().replace(/\/api$/, '');
          return `${mediaBase}/media/${doc.ruta_archivo}`;
        }
        return null;
      }, [getApiBase]);

      // Recarga la URL de la foto consultando los documentos activos del estudiante.
      // Se invoca tras subir/reemplazar/eliminar la foto desde la pestaña Documentos.
      const refreshFotoPerfil = React.useCallback(async () => {
        if (!estudianteIdRef) {
          setFotoUrl(null);
          return null;
        }
        try {
          const data = await getDocumentosEstudiante(estudianteIdRef);
          const list = Array.isArray(data) ? data : (data?.results || []);
          const foto = list.find(d => d.tipo_documento === 'FOTO_PERFIL') || list.find(d => d.tipo_documento === 'PROFILE');
          const baseUrl = resolveFotoUrlFromDoc(foto);
          if (baseUrl) {
            const sep = baseUrl.includes('?') ? '&' : '?';
            const finalUrl = `${baseUrl}${sep}v=${Date.now()}`;
            setFotoUrl(finalUrl);
            return finalUrl;
          } else {
            setFotoUrl(null);
            return null;
          }
        } catch (e) {
          // silencioso: mantenemos avatar previo
          return null;
        }
      }, [estudianteIdRef, resolveFotoUrlFromDoc]);

      const handleSelectFotoPerfilClick = () => {
        if (fotoPerfilInputRef.current) fotoPerfilInputRef.current.click();
      };

      const handleFotoPerfilChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const name = (file.name || '').toLowerCase();
        const isImage = /^image\//.test(file.type) || ['.jpg', '.jpeg', '.png', '.webp'].some((ext) => name.endsWith(ext));
        if (!isImage) {
          alert('Solo se permiten imágenes para la foto de perfil.');
          event.target.value = '';
          return;
        }

        if (!estudianteIdRef) {
          alert('No se encontró el identificador del estudiante.');
          event.target.value = '';
          return;
        }

        setUploadingFotoPerfil(true);
        try {
          const resp = await subirFotoEstudiante(estudianteIdRef, file);
          const uploadedUrl = resp?.archivo_url || '';
          if (uploadedUrl) {
            const sep = uploadedUrl.includes('?') ? '&' : '?';
            setFotoUrl(`${uploadedUrl}${sep}v=${Date.now()}`);
          } else {
            await refreshFotoPerfil();
          }
          alert('Foto de perfil guardada correctamente.');
        } catch (err) {
          console.error('Error al subir foto de perfil:', err);
          alert(`No se pudo subir la foto: ${err?.response?.data?.error || err.message}`);
        } finally {
          setUploadingFotoPerfil(false);
          event.target.value = '';
        }
      };

      // Sincronizar la foto cuando cambia el estudiante
      useEffect(() => {
        setFotoUrl(student?.foto_perfil_url || null);
      }, [student?.id_estudiante, student?.id, student?.foto_perfil_url]);

      useEffect(() => {
        if (!estudianteIdRef || mode === 'create') return;
        refreshFotoPerfil();
      }, [estudianteIdRef, mode, refreshFotoPerfil]);

      // Cargar becas activas del estudiante
      useEffect(() => {
        if (!estudianteIdRef || mode === 'create') {
          setBecasActivas([]);
          return;
        }
        
        const loadBecas = async () => {
          setCargandoBecas(true);
          try {
            const data = await getEstudianteBecas(estudianteIdRef);
            if (data && data.becas_activas && Array.isArray(data.becas_activas)) {
              setBecasActivas(data.becas_activas);
            } else {
              setBecasActivas([]);
            }
          } catch (err) {
            console.error('Error al cargar becas:', err);
            setBecasActivas([]);
          } finally {
            setCargandoBecas(false);
          }
        };
        
        loadBecas();
      }, [estudianteIdRef, mode]);

    // Sincronizar formularios cuando cambie el estudiante
    useEffect(() => {
      setGeneralForm({
        nombre_completo: student.nombre_completo || student.nombre || '',
        promedio_general: student.promedio_procedencia || student.promedio_general || '',
        fecha_nacimiento: student.fecha_nacimiento || student.fechaNacimiento || '',
        email_personal: student.email_personal || '',
        curp: student.curp || '',
        lugar_nacimiento: student.lugar_nacimiento || student.lugarNacimiento || '',
        telefono_contacto: student.telefono_principal || student.telefono_contacto || student.telefono || '',
        contacto: student.contacto || student.contacto_emergencia || student.contactoEmergencia || '',
        sexo: student.sexo || '',
        email_institucional: student.email_institucional || student.email || '',
        estado_civil: student.estado_civil || student.estadoCivil || '',
        telefono_emergencia: student.telefono_emergencia || '',
        nacionalidad: student.nacionalidad || '',
        email_tutor: student.email_tutor || student.email_padre || student.email_responsable || student.email_responsable_financiero || '',
        pais_origen: student.pais_origen || student.paisOrigen || '',
        calle: student.calle || student.direccion?.calle || '',
        estado_direccion: student.estado || student.direccion?.estado || '',
        colonia: student.colonia || student.direccion?.colonia || '',
        codigo_postal: student.codigo_postal || student.direccion?.codigoPostal || '',
        ciudad: student.ciudad || student.direccion?.ciudad || '',
        pais: student.pais || student.direccion?.pais || '',
        nombre_responsable_financiero: student.nombre_responsable_financiero || '',
        email_responsable_financiero: student.email_responsable_financiero || '',
        telefono_responsable_financiero: student.telefono_responsable_financiero || '',
        direccion_responsable_financiero: student.direccion
          ? `${student.direccion.calle || ''}, ${student.direccion.colonia || ''}, ${student.direccion.ciudad || ''}, ${student.direccion.estado || ''}, CP ${student.direccion.codigoPostal || ''}`.replace(/,\s*,/g, ',').trim()
          : '',
        nombre_padre: student.nombre_padre || '',
        ocupacion_padre: student.ocupacion_padre || '',
        nombre_madre: student.nombre_madre || '',
        ocupacion_madre: student.ocupacion_madre || '',
        ingreso_mensual_familiar: student.ingreso_mensual_familiar || '',
      });
      const cicloCandidate = (
        student.ciclo_ingreso_id ||
        student.id_ciclo_escolar ||
        (student.ciclo_ingreso && (student.ciclo_ingreso.id_ciclo_escolar || student.ciclo_ingreso.id)) ||
        student.ciclo_escolar_id ||
        student.ciclo_escolar ||
        ''
      );
      const programaCandidate = (
        student.programa || student.programa_id || student.programa_nombre || student.id_programa_actual || student.id_programa_actual_id || ''
      );
      const seccionCandidate = (
        student.seccion || student.seccion_id || student.id_seccion || student.id_seccion_id || student.informacionEscolar?.seccion || ''
      );
      // Resolver IDs incluso si vienen en objetos con claves distintas
      const resolveNumericId = (candidate, keys = []) => {
        if (candidate === undefined || candidate === null) return null;
        if (typeof candidate === 'number' && !isNaN(candidate)) return Number(candidate);
        if (typeof candidate === 'string' && candidate.trim() !== '' && !isNaN(Number(candidate))) return Number(candidate);
        if (typeof candidate === 'object') {
          for (const k of keys) {
            if (candidate[k] !== undefined && candidate[k] !== null && !isNaN(Number(candidate[k]))) return Number(candidate[k]);
          }
        }
        return null;
      };

      const institucionId = resolveNumericId(student.id_institucion || student.institucion || student.informacionEscolar?.institucion, ['id_institucion', 'id', 'pk']);
      const campusId = resolveNumericId(student.id_campus || student.campus || student.informacionEscolar?.campus, ['id_campus', 'id', 'pk']);

      setEscolarForm({
        matricula: student.matricula || '',
        promedio_general: student.promedio_procedencia || student.promedio_general || '',
        ciclo_ingreso: cicloCandidate ? String(cicloCandidate) : '',
        programa: programaCandidate ? String(programaCandidate) : '',
        seccion: seccionCandidate ? String(seccionCandidate) : '',
        estatus_estudiante: student.estatus_estudiante || student.status || '',
        tipo_estudiante: student.tipo_estudiante || student.tipoEstudiante || '',
        turno: normalizeTurno(student.turno),
        modalidad: normalizeModalidad(student.modalidad),
        institucion: institucionId ? String(institucionId) : (student.institucion || ''),
        campus: campusId ? String(campusId) : (student.campus || student.campus_nombre || ''),
      });
    }, [student]);
  // Estado global para todos los campos de InformacionGeneral
  const [generalForm, setGeneralForm] = useState({
    nombre_completo: student.nombre_completo || student.nombre || '',
    // En el backend el campo real es promedio_procedencia
    promedio_general: student.promedio_procedencia || student.promedio_general || '',
    fecha_nacimiento: student.fecha_nacimiento || student.fechaNacimiento || '',
    email_personal: student.email_personal || '',
    curp: student.curp || '',
    lugar_nacimiento: student.lugar_nacimiento || student.lugarNacimiento || '',
    // En el backend el campo se llama telefono_principal
    telefono_contacto: student.telefono_principal || student.telefono_contacto || student.telefono || '',
    sexo: student.sexo || '',
    email_institucional: student.email_institucional || student.email || '',
    estado_civil: student.estado_civil || student.estadoCivil || '',
    telefono_emergencia: student.telefono_emergencia || '',
    nacionalidad: student.nacionalidad || '',
    email_tutor: student.email_tutor || student.email_padre || student.email_responsable || student.email_responsable_financiero || '',
    contacto: student.contacto || student.contacto_emergencia || student.contactoEmergencia || '',
    pais_origen: student.pais_origen || student.paisOrigen || '',
    // Dirección: usar directamente los campos del modelo si existen
    calle: student.calle || student.direccion?.calle || '',
    estado_direccion: student.estado || student.direccion?.estado || '',
    colonia: student.colonia || student.direccion?.colonia || '',
    codigo_postal: student.codigo_postal || student.direccion?.codigoPostal || '',
    ciudad: student.ciudad || student.direccion?.ciudad || '',
    pais: student.pais || student.direccion?.pais || '',
    nombre_responsable_financiero: student.nombre_responsable_financiero || '',
    email_responsable_financiero: student.email_responsable_financiero || '',
    telefono_responsable_financiero: student.telefono_responsable_financiero || '',
    direccion_responsable_financiero: student.direccion
      ? `${student.direccion.calle || ''}, ${student.direccion.colonia || ''}, ${student.direccion.ciudad || ''}, ${student.direccion.estado || ''}, CP ${student.direccion.codigoPostal || ''}`.replace(/,\s*,/g, ',').trim()
      : '',
    // Datos de Padres o Tutores
    nombre_padre: student.nombre_padre || '',
    ocupacion_padre: student.ocupacion_padre || '',
    nombre_madre: student.nombre_madre || '',
    ocupacion_madre: student.ocupacion_madre || '',
    ingreso_mensual_familiar: student.ingreso_mensual_familiar || '',
  });

  // Estado global para todos los campos de InformacionEscolar
  const [escolarForm, setEscolarForm] = useState({
    matricula: student.matricula || '',
    promedio_general: student.promedio_general || '',
    ciclo_ingreso: (
      student.ciclo_ingreso_id ||
      student.id_ciclo_escolar ||
      (student.ciclo_ingreso && (student.ciclo_ingreso.id_ciclo_escolar || student.ciclo_ingreso.id)) ||
      student.ciclo_escolar_id ||
      student.ciclo_escolar ||
      ''
    ),
    programa: (
      student.programa || student.programa_id || student.programa_nombre || student.id_programa_actual || student.id_programa_actual_id || ''
    ),
    seccion: (
      student.seccion || student.seccion_id || student.id_seccion || student.id_seccion_id || student.informacionEscolar?.seccion || ''
    ),
    estatus_estudiante: student.estatus_estudiante || student.status || '',
    tipo_estudiante: (function(){
      const v = student.tipo_estudiante || student.tipoEstudiante || '';
      if (!v) return '';
      const map = {
        'nuevo ingreso': 'NUEVO_INGRESO',
        'nuevo_ingreso': 'NUEVO_INGRESO',
        'reincorporado': 'REINCORPORADO',
        'promovido': 'PROMOVIDO',
        'repetidor': 'REPETIDOR',
        'egresado': 'EGRESADO'
      };
      const s = String(v).trim();
      if (map[s.toLowerCase()]) return map[s.toLowerCase()];
      return s;
    })(),
    turno: normalizeTurno(student.turno),
    modalidad: normalizeModalidad(student.modalidad),
    institucion: student.id_institucion || student.institucion || student.informacionEscolar?.institucion || '',
    campus: student.id_campus || student.campus || student.campus_nombre || '',
  });

  // Handler para actualizar cualquier campo del formulario general
  const handleGeneralChange = (field, value) => {
    setGeneralForm(prev => ({ ...prev, [field]: value }));
  };
  // Handler para actualizar cualquier campo del formulario escolar
  const handleEscolarChange = (field, value) => {
    // Forzar que institucion siempre sea string de id numérico
    if (field === 'institucion') {
      setEscolarForm(prev => ({ ...prev, institucion: value && !isNaN(Number(value)) ? String(value) : '' }));
    } else {
      setEscolarForm(prev => ({ ...prev, [field]: value }));
    }
  };

  // Referencias para obtener datos de los tabs (si se usan en otros tabs)
  const infoGeneralRef = useRef();
  const infoEscolarRef = useRef();
  const fichaMedicaRef = useRef();
  React.useEffect(() => {
    if (mode === 'edit') {
      console.log('[STUDENT MODAL] escolarForm inicial:', escolarForm);
    }
  }, [mode, escolarForm]);
  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';
  const [activeTab, setActiveTab] = useState('informacion-general');
  const [generatedPassword, setGeneratedPassword] = useState(null);
  // Estatus efectivo: prioriza el valor del form (reactivo) y cae al del estudiante cargado.
  // El acceso al Portal está acoplado a estatus_estudiante (única fuente de verdad).
  const ESTATUS_CON_ACCESO_PORTAL = ['ACTIVO', 'PASANTE'];
  const effectiveEstatus = String(
    escolarForm.estatus_estudiante || student.estatus_estudiante || ''
  ).toUpperCase();
  const isAccountActive = ESTATUS_CON_ACCESO_PORTAL.includes(effectiveEstatus);
  const setIsAccountActive = (nextOrUpdater) => {
    if (readOnly) return;
    const nextValue = typeof nextOrUpdater === 'function' ? nextOrUpdater(isAccountActive) : nextOrUpdater;
    // Activar => ACTIVO. Desactivar => BAJA_TEMPORAL (estatus reversible para corte de acceso).
    // Si el estudiante ya está en un estatus terminal (BAJA_DEFINITIVA / EGRESADO / NO_INSCRITO),
    // no se sobreescribe al desactivar: ese cambio se hace desde el dropdown de estatus.
    if (nextValue) {
      handleEscolarChange('estatus_estudiante', 'ACTIVO');
    } else if (effectiveEstatus === 'ACTIVO' || effectiveEstatus === 'PASANTE' || effectiveEstatus === '') {
      handleEscolarChange('estatus_estudiante', 'BAJA_TEMPORAL');
    }
  };
  const displayName = isCreateMode
    ? student.nombre_completo || student.nombre || 'Nuevo estudiante'
    : student.nombre_completo || student.nombre || 'Sin nombre';
  const displayStatus = !isCreateMode
    ? (effectiveEstatus || student.estatus_estudiante || null)
    : null;
  // Función para generar contraseña aleatoria segura
  function generarPassword(longitud = 8) {
    const caracteres = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=';
    let password = '';
    for (let i = 0; i < longitud; i++) {
      password += caracteres.charAt(Math.floor(Math.random() * caracteres.length));
    }
    return password;
  }
  const handleGenerateCredential = () => {
    setGeneratedPassword(generarPassword(8));
  };

  const handleSave = async () => {
        // Depuración: mostrar valor actual de escolarForm antes de validar
        console.log('[HANDLE SAVE] escolarForm antes de validar:', escolarForm);
        // Validar institución antes de enviar
        const payloadPreview = mapStudentFormToPayload(student, generalForm, escolarForm, isEditMode);
        console.log('[HANDLE SAVE] payloadPreview.id_institucion:', payloadPreview.id_institucion);
        if (!payloadPreview.id_institucion) {
          setErroredFields(['id_institucion']);
          alert('Error al guardar estudiante:\nLa institución es obligatoria.');
          return;
        }
    // Validar campos obligatorios
    const requiredFields = [
      { field: 'nombre_completo', label: 'Nombre Completo', form: generalForm },
      { field: 'fecha_nacimiento', label: 'Fecha de Nacimiento', form: generalForm },
      { field: 'lugar_nacimiento', label: 'Lugar de Nacimiento', form: generalForm },
      { field: 'sexo', label: 'Género', form: generalForm },
    ];
    
    const requiredEscolarFields = [
      // Matrícula solo es obligatoria en modo crear, no en edición
      ...(isCreateMode ? [{ field: 'matricula', label: 'Matrícula', form: escolarForm }] : []),
      // ciclo_ingreso solo es obligatorio en modo crear; en edit es read-only (valor histórico inmutable)
      ...(isCreateMode ? [{ field: 'ciclo_ingreso', label: 'Ciclo Escolar', form: escolarForm }] : []),
      { field: 'programa', label: 'Programa Académico', form: escolarForm },
      { field: 'institucion', label: 'Institución', form: escolarForm },
      { field: 'campus', label: 'Campus', form: escolarForm },
    ];
    
    const allRequiredFields = [...requiredFields, ...requiredEscolarFields];
    const isBlank = (value) => {
      if (value === undefined || value === null) return true;
      if (typeof value === 'string') return value.trim() === '';
      return value === '';
    };
    const missingFields = allRequiredFields.filter(f => isBlank(f.form[f.field]));
    
    if (missingFields.length > 0) {
      const missingLabels = missingFields.map(f => f.label).join(', ');
      alert(`Por favor complete los siguientes campos obligatorios: ${missingLabels}`);
      return;
    }
    
    let general = generalForm;
    let escolar = escolarForm;
    const rawData = { ...student, ...general, ...escolar };
    // Construir payload correctamente usando los 4 argumentos
    const data = mapStudentFormToPayload(student, general, escolar, isEditMode);

    // Incluir campos de Ficha Médica si el componente está montado
    if (fichaMedicaRef.current && fichaMedicaRef.current.getFormPayload) {
      const fmPayload = fichaMedicaRef.current.getFormPayload();
      Object.assign(data, fmPayload);
    }

    console.log('Payload enviado al backend:', data);
    try {
      if (isCreateMode) {
        await createStudent(data);
        alert('Estudiante registrado correctamente');
      } else if (isEditMode) {
        // Para edición usamos PATCH para no exigir todos los campos obligatorios
        await patchStudent(student.id_estudiante || student.id, data);
        alert('Estudiante actualizado correctamente');
      }
      if (onSave) onSave();
      onClose();
    } catch (err) {
      let msg = err?.message || 'Error desconocido al guardar.';
      // Mapeo de errores técnicos a mensajes amigables
      const friendlyErrors = {
        email_institucional: 'Ya existe un estudiante con este correo institucional.',
        ingreso_mensual_familiar: 'El ingreso mensual familiar debe tener máximo 12 dígitos.',
        id_institucion: 'La institución es obligatoria.',
        codigo_postal: 'El código postal debe tener máximo 10 caracteres.',
      };
      let fieldsWithError = [];
      if (err?.response && err.response.data) {
        if (typeof err.response.data === 'string') {
          msg = err.response.data;
        } else if (typeof err.response.data === 'object') {
          msg = Object.entries(err.response.data)
            .map(([k, v]) => {
              if (friendlyErrors[k]) {
                fieldsWithError.push(k);
                return friendlyErrors[k];
              }
              fieldsWithError.push(k);
              return `${k}: ${Array.isArray(v) ? v.join(', ') : v}`;
            })
            .join('\n');
        }
      }
      setErroredFields(fieldsWithError);
      alert('Error al guardar estudiante:\n' + msg);
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const tabs = [
    { id: 'informacion-general', label: 'Información General' },
    { id: 'informacion-escolar', label: 'Información Escolar' },
    { id: 'calificaciones', label: 'Calificaciones' },
    { id: 'estado-financiero', label: 'Estado de Cuenta' },
    { id: 'documentos', label: 'Documentos' },
    { id: 'ficha-medica', label: 'Ficha Médica' },
    { id: 'expediente-alumno', label: 'Expediente del Alumno' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'informacion-general':
        return <InformacionGeneral
          values={generalForm}
          onChange={handleGeneralChange}
          student={student}
          mode={mode}
          erroredFields={erroredFields}
        />;
      case 'informacion-escolar':
        return <InformacionEscolar
          values={escolarForm}
          onChange={handleEscolarChange}
          student={student}
          mode={mode}
          erroredFields={erroredFields}
        />;
      case 'calificaciones':
        return <Calificaciones student={student} />;
      case 'estado-financiero':
        return <EstadoFinanciero student={student} />;
      case 'documentos':
        return <Documentos student={student} onFotoChanged={refreshFotoPerfil} />;
      case 'ficha-medica':
        return <FichaMedica ref={fichaMedicaRef} student={student} mode={mode} fotoUrl={fotoUrl} onFotoChanged={refreshFotoPerfil} />;
      case 'expediente-alumno':
        return <ExpedienteAlumno student={student} fotoUrl={fotoUrl} onFotoChanged={refreshFotoPerfil} />;
      default:
        return <InformacionGeneral ref={infoGeneralRef} student={student} mode={mode} erroredFields={erroredFields} />;
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalContent}>
          {/* Header estilo "Nueva Materia" */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              paddingLeft: 32,
              margin: 0,
              backgroundColor: '#f5f5f7',
              borderBottom: '1px solid #e5e5ea',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              position: 'sticky',
              top: 0,
              zIndex: 5
            }}
          >
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#1d1d1f',
                  letterSpacing: '-0.2px'
                }}
              >
                {isCreateMode
                  ? 'Registrar Estudiante'
                  : isEditMode
                  ? 'Editar Estudiante'
                  : 'Detalle del Estudiante'}
              </h2>
            </div>
            <button
              type="button"
              aria-label="Cerrar"
              onClick={onClose}
              style={{
                border: 'none',
                background: 'transparent',
                width: 32,
                height: 32,
                borderRadius: '999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#1d1d1f',
                fontSize: 18,
                transition: 'background 0.2s ease, transform 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f2f2f7';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
              }}
            >
              ✕
            </button>
          </div>

          {/* Aquí inicia el contenido principal del modal de estudiante */}
          {/* Encabezado del estudiante + alerta financiera + pestañas fijados */}
          <div
            style={{
              position: 'sticky',
              top: 48,
              zIndex: 4,
              backgroundColor: '#ffffff'
            }}
          >
            {/* Student Header Card */}
            <div className={styles.studentHeader}>
              <div className={styles.studentHeaderContent}>
                <div
                  className={styles.studentAvatar}
                  onClick={() => {
                    if (!isCreateMode && !readOnly) {
                      handleSelectFotoPerfilClick();
                    }
                  }}
                  style={{ cursor: !isCreateMode && !readOnly ? 'pointer' : 'default', position: 'relative' }}
                  title={!isCreateMode && !readOnly ? 'Cambiar imagen de perfil' : ''}
                >
                  {fotoUrl ? (
                    <img
                      src={fotoUrl}
                      alt="Fotografía del estudiante"
                      className={styles.studentAvatarImg}
                      onError={() => setFotoUrl(null)}
                    />
                  ) : (
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" fill="#e0e0e0"/>
                      <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" fill="#898d8d"/>
                      <path d="M6 21V19C6 17.9391 6.42143 16.9217 7.17157 16.1716C7.92172 15.4214 8.93913 15 10 15H14C15.0609 15 16.0783 15.4214 16.8284 16.1716C17.5786 16.9217 18 17.9391 18 19V21" fill="#898d8d"/>
                    </svg>
                  )}
                  {!isCreateMode && !readOnly && (
                    <span
                      style={{
                        position: 'absolute',
                        bottom: -8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(0,0,0,0.65)',
                        color: '#fff',
                        fontSize: 10,
                        padding: '2px 6px',
                        borderRadius: 6,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Cambiar
                    </span>
                  )}
                </div>
                <div className={styles.studentHeaderInfo}>
                  <div className={styles.studentNameRow}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <h2 className={styles.studentName}>{displayName}</h2>
                      {displayStatus && (
                        <span className={styles.statusBadge}>{displayStatus}</span>
                      )}
                    </div>
                  </div>
                  <div className={styles.studentContactInfo}>
                    <div className={styles.contactItem}>
                      <span className={styles.contactLabel}>Matrícula:</span>
                      <span className={styles.contactValue}>
                        {isCreateMode ? 'Pendiente' : (student.matricula || 'No disponible')}
                      </span>
                    </div>
                    <div className={styles.contactItem}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z" stroke="#898d8d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className={styles.contactValue}>
                        {isCreateMode
                          ? 'Correo pendiente'
                          : (student.email_institucional || generalForm.email_institucional || student.email || 'No disponible')}
                      </span>
                    </div>
                    <div className={styles.contactItem}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 5C3 3.89543 3.89543 3 5 3H8.27924C8.70967 3 9.09181 3.27543 9.22792 3.68377L10.7257 8.17721C10.8831 8.64932 10.6694 9.16531 10.2243 9.38787L7.96701 10.5165C9.06925 12.9612 11.0388 14.9308 13.4835 16.033L14.6121 13.7757C14.8347 13.3306 15.3507 13.1169 15.8228 13.2743L20.3162 14.7721C20.7246 14.9082 21 15.2903 21 15.7208V19C21 20.1046 20.1046 21 19 21H18C9.71573 21 3 14.2843 3 6V5Z" stroke="#898d8d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className={styles.contactValue}>
                        {isCreateMode
                          ? 'Teléfono pendiente'
                          : (student.telefono_principal || generalForm.telefono_contacto || student.telefono_contacto || student.telefono || 'No disponible')}
                      </span>
                    </div>
                  </div>
                  {!isCreateMode && !readOnly && (
                    <div style={{ marginTop: 10 }}>
                      <input
                        ref={fotoPerfilInputRef}
                        type="file"
                        accept="image/*,.jpg,.jpeg,.png,.webp"
                        style={{ display: 'none' }}
                        onChange={handleFotoPerfilChange}
                      />
                      <button
                        type="button"
                        onClick={handleSelectFotoPerfilClick}
                        disabled={uploadingFotoPerfil}
                        style={{
                          border: '1px solid rgba(255,255,255,0.45)',
                          background: 'rgba(255,255,255,0.12)',
                          color: '#fff',
                          borderRadius: 8,
                          padding: '6px 10px',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: uploadingFotoPerfil ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {uploadingFotoPerfil ? 'Subiendo foto...' : 'Editar imagen de perfil'}
                      </button>
                    </div>
                  )}
                  {/* Controles de acceso al portal y generación de password */}
                  <div
                    style={{
                      marginTop: 16,
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: 16
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: 'rgba(255,255,255,0.9)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      >
                        Acceso al portal
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: 'rgba(255,255,255,0.9)',
                          opacity: 0.9
                        }}
                      >
                        {isAccountActive ? 'Habilitado' : 'Deshabilitado'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsAccountActive(prev => !prev)}
                      disabled={readOnly}
                      style={{
                        position: 'relative',
                        width: '46px',
                        height: '26px',
                        borderRadius: '999px',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        background: isAccountActive ? '#34c759' : 'rgba(255,255,255,0.4)',
                        boxShadow: isAccountActive
                          ? 'inset 0 0 0 1px rgba(0,0,0,0.05)'
                          : 'inset 0 0 0 1px rgba(0,0,0,0.08)',
                        transition: 'background 0.25s ease, box-shadow 0.25s ease'
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '3px',
                          left: isAccountActive ? '23px' : '3px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#fff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                          transition: 'left 0.25s ease'
                        }}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isAccountActive || readOnly) return;
                        handleGenerateCredential();
                      }}
                      style={{
                        background: isAccountActive
                          ? 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)'
                          : 'linear-gradient(135deg, rgba(159,163,169,0.9) 0%, rgba(142,142,147,0.9) 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '999px',
                        padding: '8px 18px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: isAccountActive && !readOnly ? 'pointer' : 'not-allowed',
                        boxShadow: isAccountActive
                          ? '0 4px 10px rgba(0,122,255,0.45)'
                          : '0 2px 5px rgba(0,0,0,0.25)',
                        letterSpacing: '0.3px',
                        opacity: isAccountActive && !readOnly ? 1 : 0.7,
                        transition: 'all 0.25s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!isAccountActive || readOnly) return;
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 6px 14px rgba(0,122,255,0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = isAccountActive
                          ? '0 4px 10px rgba(0,122,255,0.45)'
                          : '0 2px 5px rgba(0,0,0,0.25)';
                      }}
                    >
                      🔐 Generar Password
                    </button>
                  </div>
                </div>
              </div>
              <div className={styles.studentHeaderRight}>
                <div className={styles.scholarshipCard}>
                  <div className={styles.scholarshipLabel}>Becas Activas</div>
                  <div className={styles.scholarshipValue} style={{ fontSize: becasActivas?.length > 1 ? '12px' : '16px' }}>
                    {cargandoBecas ? (
                      <span style={{ fontSize: '12px' }}>Cargando...</span>
                    ) : becasActivas && becasActivas.length > 0 ? (
                      becasActivas.map((beca, idx) => (
                        <div key={beca.id_estudiante_beca || idx} style={{ marginBottom: idx < becasActivas.length - 1 ? '6px' : 0 }}>
                          <span 
                            style={{ 
                              display: 'block',
                              fontSize: '13px',
                              fontWeight: 500
                            }}
                            title={`${beca.porcentaje_otorgado ? `${beca.porcentaje_otorgado}%` : beca.monto_beca_asignado ? `$${parseFloat(beca.monto_beca_asignado).toLocaleString('es-MX')}` : 'Sin monto especificado'}`}
                          >
                            {beca.nombre_beca}
                          </span>
                          <span 
                            style={{ 
                              display: 'block',
                              fontSize: '11px',
                              opacity: 0.85,
                              marginTop: '2px'
                            }}
                          >
                            {beca.porcentaje_otorgado ? `${beca.porcentaje_otorgado}%` : ''}
                            {beca.monto_beca_asignado ? `$${parseFloat(beca.monto_beca_asignado).toLocaleString('es-MX')}` : ''}
                          </span>
                        </div>
                      ))
                    ) : (
                      <span style={{ fontSize: '12px', opacity: 0.8 }}>Sin becas activas</span>
                    )}
                  </div>
                  {/* Barra de progreso solo si hay becas con porcentaje */}
                  {becasActivas?.length > 0 && becasActivas.some(b => b.porcentaje_otorgado > 0) && (
                    <div className={styles.scholarshipProgress}>
                      <div 
                        className={styles.scholarshipProgressBar} 
                        style={{ 
                          width: `${Math.min(becasActivas.reduce((sum, b) => sum + (parseFloat(b.porcentaje_otorgado) || 0), 0), 100)}%`
                        }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Financial Health Alert */}
            {(student.saludFinanciera || student.estado_financiero?.saldo_vencido > 0) && (
              <div className={styles.financialAlert}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V11M12 15H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0377 2.66667 10.2679 4L3.33975 16C2.56995 17.3333 3.53223 19 5.07183 19Z" stroke="#ffc107" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className={styles.financialAlertText}>
                  <strong>Salud Financiera:</strong> {student.saludFinanciera || 
                    (student.estado_financiero?.saldo_vencido > 0 
                      ? `El estudiante tiene $${student.estado_financiero.saldo_vencido.toFixed(2)} de saldo vencido.` 
                      : 'Al corriente'
                    )
                  }
                </span>
              </div>
            )}

            {/* Tabs Navigation */}
            <div className={styles.tabsContainer}>
              <div className={styles.tabsNav}>
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`${styles.tabButton} ${activeTab === tab.id ? styles.tabButtonActive : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className={styles.tabContent}>{renderTabContent()}</div>

          {/* Footer de acciones (Guardar / Cancelar) fijo al fondo, solo en crear/editar */}
          {(isCreateMode || isEditMode) && (
            <div
              style={{
                position: 'sticky',
                bottom: 0,
                backgroundColor: '#ffffff',
                padding: '12px 24px',
                borderTop: '1px solid #e5e5ea',
                marginTop: 8,
                zIndex: 3,
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <div className={programStyles.modalActions} style={{ paddingTop: 0 }}>
                <button
                  type="button"
                  className={programStyles.primaryButton}
                  onClick={handleSave}
                >
                  GUARDAR
                </button>
                <button
                  type="button"
                  className={programStyles.secondaryButton}
                  onClick={onClose}
                >
                  CANCELAR
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Overlay para mostrar la contraseña generada como ventana superpuesta */}
        {generatedPassword && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.45)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              animation: 'fadeIn 0.25s ease-out'
            }}
            onClick={(e) => {
              e.stopPropagation();
              setGeneratedPassword(null);
            }}
          >
            <div
              style={{
                backgroundColor: 'rgba(255,255,255,0.98)',
                borderRadius: '18px',
                padding: '24px 28px',
                boxShadow: '0 18px 50px rgba(0,0,0,0.35)',
                minWidth: '320px',
                maxWidth: '420px',
                textAlign: 'center',
                animation: 'slideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6e6e73',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '12px'
                }}
              >
                Contraseña generada
              </div>
              <div style={{ marginBottom: '16px' }}>
                <code
                  style={{
                    fontFamily: 'SF Mono, Monaco, Menlo, monospace',
                    fontSize: '22px',
                    fontWeight: 600,
                    color: '#007AFF',
                    letterSpacing: '1px',
                    backgroundColor: '#f5f5f7',
                    padding: '10px 18px',
                    borderRadius: '10px',
                    border: '1px solid #d2d2d7',
                    display: 'inline-block'
                  }}
                >
                  {generatedPassword}
                </code>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  style={{
                    background: 'white',
                    border: '1px solid #d2d2d7',
                    borderRadius: '10px',
                    padding: '8px 14px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#007AFF';
                    e.target.style.borderColor = '#007AFF';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'white';
                    e.target.style.borderColor = '#d2d2d7';
                    e.target.style.color = '#000';
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(generatedPassword);
                    const btn = e.target;
                    const originalText = btn.textContent;
                    btn.textContent = '✓ Copiado';
                    btn.style.background = '#34c759';
                    btn.style.borderColor = '#34c759';
                    btn.style.color = '#fff';
                    setTimeout(() => {
                      btn.textContent = originalText;
                      btn.style.background = 'white';
                      btn.style.borderColor = '#d2d2d7';
                      btn.style.color = '#000';
                    }, 1500);
                  }}
                >
                  📋 Copiar
                </button>
                <button
                  style={{
                    background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '8px 18px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 122, 255, 0.3)',
                    transition: 'all 0.25s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(0, 122, 255, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 122, 255, 0.3)';
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setGeneratedPassword(null);
                  }}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentDetailModal;
